import React, { useCallback } from 'react';
import { Bot, Mail, MessageSquare, Plug, Search, Share2, ShieldCheck, Zap } from 'lucide-react';
import T from 'Components/Helpers/translate';
import AgentApprovalsResource from 'Resources/AgentApprovals';
import ProposalCardShell, { type StatusPill } from 'App/shadcn/pages/AIWorkspace/components/ProposalCardShell';
import { capabilityLabel } from 'App/shadcn/pages/AIWorkspace/AgentConfig/autonomyGrants';
import { executionFailureMessage } from './approvalAdapters';
import { formatFieldName } from './FieldGrid';
import type { ToolProposal } from './types';

type Props = {
	proposal: ToolProposal;
	onUpdate?: (update: Partial<ToolProposal>) => void;
};

const TOOL_ICONS: Record<string, React.ComponentType<{ style?: React.CSSProperties }>> = {
	sendUserEmail: Mail,
	sendSlackMessage: MessageSquare,
	lookupContactDetails: Search,
	connectionInvoke: Plug,
	manageAutomations: Zap,
	manageAgents: Bot,
	delegateTask: Share2
};

/** `T` hands back the key on a miss; the keys are data, so fall back then. */
const translateOr = (key: string, fallback: string) => {
	const label = T(key);
	return label === key ? fallback : label;
};

function statusToPill(status: ToolProposal['status']): StatusPill {
	switch (status) {
		case 'saving':
			return { label: T('assistant.saving'), tone: 'saving' };
		case 'saved':
			return { label: T('assistant.approval.statusApproved'), tone: 'ok' };
		case 'cancelled':
			return { label: T('assistant.approval.statusRejected'), tone: 'muted' };
		case 'error':
			return { label: T('default.failed'), tone: 'error' };
		case 'pending':
		default:
			return { label: T('assistant.awaitingApproval'), tone: 'pending' };
	}
}

/**
 * WEB-12517 — a chat tool call the agent's permissions don't cover. Generic on
 * purpose: the backend describes the call as label/value lines, so one card
 * serves every gated tool. Nothing is editable (the backend refuses edits for
 * these actions), and on approve the backend runs the tool and resumes the agent.
 */
const ToolProposalCard = ({ proposal, onUpdate }: Props) => {
	const isRunning = proposal.status === 'saving';
	const resolved = proposal.status === 'saved' || proposal.status === 'cancelled';
	const errored = proposal.status === 'error';

	const run = useCallback(
		async (call: () => Promise<{ approval: { resultPayload?: unknown } }>) => {
			if (!onUpdate) return;
			onUpdate({ status: 'saving', error: undefined });
			try {
				const { approval } = await call();
				const failure = executionFailureMessage(
					approval.resultPayload as Record<string, unknown> | null | undefined
				);
				onUpdate(
					failure !== null ? { status: 'error', error: failure || T('default.failed') } : { status: 'saved' }
				);
			} catch (err) {
				onUpdate({ status: 'error', error: err instanceof Error ? err.message : T('default.failed') });
			}
		},
		[onUpdate]
	);

	// No modified payload: these actions run exactly as proposed.
	const handleApprove = useCallback(
		() => run(() => AgentApprovalsResource.approve(proposal.approvalId)),
		[proposal.approvalId, run]
	);
	// A failed row is terminal server-side; the retry endpoint re-executes it (WEB-9873).
	const handleRetry = useCallback(
		() => run(() => AgentApprovalsResource.retry(proposal.approvalId)),
		[proposal.approvalId, run]
	);

	const handleReject = useCallback(async () => {
		if (!onUpdate) return;
		onUpdate({ status: 'saving', error: undefined });
		try {
			await AgentApprovalsResource.reject(proposal.approvalId, 'cancelled_in_chat');
			onUpdate({ status: 'cancelled' });
		} catch (err) {
			// Still pending on the server, so both buttons stay.
			onUpdate({ status: 'pending', error: err instanceof Error ? err.message : T('default.failed') });
		}
	}, [proposal.approvalId, onUpdate]);

	// "Message colleagues" reads as a label; mid-sentence it is "message colleagues".
	const capability = proposal.capability ? capabilityLabel(proposal.capability) : '';
	const hint = capability
		? `${T('assistant.toolProposal.hint', {
				capability: capability.charAt(0).toLocaleLowerCase() + capability.slice(1)
		  })} ${T('assistant.toolProposal.hintSuffix')}`
		: null;

	const body = (
		<div className="pr-email-body">
			{proposal.error && <div className="pr-email-error">{proposal.error}</div>}
			{proposal.fields.length > 0 && (
				<dl className="pr-email-fields">
					{proposal.fields.map((f, idx) => (
						<React.Fragment key={idx}>
							<dt className="self-start pt-0.5">
								{translateOr(
									`assistant.toolProposal.field.${f.key}`,
									translateOr(`default.${f.key}`, formatFieldName(f.key))
								)}
							</dt>
							<dd className="whitespace-pre-line break-words">{f.value}</dd>
						</React.Fragment>
					))}
				</dl>
			)}
			{hint && !resolved && (
				<p className="mt-3 flex items-start gap-1.5 text-body-sm text-muted-foreground">
					<ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
					<span>{hint}</span>
				</p>
			)}
		</div>
	);

	return (
		<div className="pr-email-card-wrapper">
			<ProposalCardShell
				icon={TOOL_ICONS[proposal.tool] ?? ShieldCheck}
				title={translateOr(`assistant.toolProposal.title.${proposal.tool}`, proposal.tool)}
				statusPill={statusToPill(proposal.status)}
				body={body}
				busy={isRunning}
				resolved={resolved}
				approveLabel={errored ? T('assistant.retry') : T('assistant.approve')}
				rejectLabel={T('assistant.reject')}
				onApprove={resolved || isRunning ? undefined : errored ? handleRetry : handleApprove}
				onReject={resolved || errored ? undefined : handleReject}
				defaultExpanded
			/>
		</div>
	);
};

export default ToolProposalCard;
